import * as fmt from '../i18n/format';

/**
 * Utility functions for date and time calculations
 */

/**
 * Fronteira com o banco: `DATE` do Postgres é dia civil, sem hora e sem fuso.
 *
 * Estas duas funções são a ÚNICA travessia permitida entre a string `YYYY-MM-DD`
 * e o `Date` do JavaScript. Os dois atalhos ingênuos erram o dia inteiro em
 * UTC-3: `new Date("2026-09-23")` devolve 22/09 21:00 (a string ISO pura é lida
 * como meia-noite UTC) e `toISOString()` na volta empurra para o dia seguinte em
 * fuso positivo. Quem reimplementar isto por fora reintroduz o bug.
 */
export const formatDateForDB = (date: Date | string | null): string | null => {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return null;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const parseDateFromDB = (dateStr: string | Date | null): Date | null => {
  if (!dateStr) return null;
  if (dateStr instanceof Date) return dateStr;
  const parts = dateStr.split('-');
  if (parts.length !== 3) return new Date(dateStr);
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // Mês é 0-indexed
  const day = parseInt(parts[2], 10);
  return new Date(year, month, day);
};

export const addDays = (date: Date | string, days: number): Date => {
  const result = new Date(date);
  // Retornar data atual se inválida
  if (isNaN(result.getTime())) return new Date();
  result.setDate(result.getDate() + days);
  return result;
};

export const addMonths = (date: Date | string, months: number): Date => {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
};

export const formatDate = (date: Date | string | null | undefined, format: string = 'dd/MM/yyyy'): string => {
  if (!date) return '';
  const d = new Date(date);
  // Retornar vazio se data inválida
  if (isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();

  if (format === 'dd/MM/yyyy') {
    return `${day}/${month}/${year}`;
  }
  return fmt.formatDate(d);
};

export const diffDays = (date1: Date | string, date2: Date | string): number => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  // Validar se ambas as datas são válidas
  if (isNaN(d1.getTime()) || isNaN(d2.getTime())) {
    return 0;
  }
  const diff = Math.abs(d2.getTime() - d1.getTime());
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

export const getWeekdaysBetween = (start: Date | string, end: Date | string, workDays: number[] = [1, 2, 3, 4, 5]): number => {
  let count = 0;
  const current = new Date(start);
  const endDate = new Date(end);

  while (current <= endDate) {
    const day = current.getDay();
    if (workDays.includes(day)) {
      count++;
    }
    current.setDate(current.getDate() + 1);
  }
  return count;
};

export const isWeekday = (date: Date | string, workDays: number[] = [1, 2, 3, 4, 5]): boolean => {
  const day = new Date(date).getDay();
  return workDays.includes(day);
};

/**
 * Find the next work day from a given date
 * If the date is already a work day (and not a holiday), returns the same date
 */
export const getNextWorkDay = (date: Date | string, workDays: number[] = [1, 2, 3, 4, 5], holidays: string[] = []): Date => {
  const current = new Date(date);
  while (!workDays.includes(current.getDay()) || isDateHoliday(current, holidays)) {
    current.setDate(current.getDate() + 1);
  }
  return current;
};

export const getMonthName = (date: Date | string, short: boolean = true): string => {
  const months = short
    ? ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
    : ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
  return months[new Date(date).getMonth()];
};

export const getDaysInMonth = (date: Date | string): number => {
  const d = new Date(date);
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
};

/**
 * Meia-noite local do dia.
 *
 * As datas de tarefa chegam do banco em meia-noite local (`parseDateFromDB`),
 * mas qualquer `new Date()` de navegação carrega a hora do acesso. Misturar os
 * dois numa subtração de dias é o que fazia a barra do Cronograma cair uma
 * coluna para a esquerda em todo horário que não fosse meia-noite exata.
 */
export const startOfDay = (date: Date | string): Date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

/** Segunda-feira da semana da data, sempre em meia-noite local. */
export const getWeekStart = (date: Date | string): Date => {
  const d = startOfDay(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff));
};

/**
 * Domingo da mesma semana. Avança por `setDate`, não somando 6 x 86400000, para
 * não escorregar uma hora em fuso com horário de verão (o produto roda em EN e ES).
 */
export const getWeekEnd = (date: Date | string): Date => {
  const end = getWeekStart(date);
  end.setDate(end.getDate() + 6);
  return end;
};

/**
 * Índice da coluna (0 = segunda) de uma data dentro da semana que começa em
 * `weekStart`. `round`, não `floor`: as duas pontas são meia-noite, então a
 * divisão só deixa de ser inteira no dia de 23h ou 25h de quem tem horário de verão.
 */
export const weekDayIndex = (weekStart: Date, date: Date | string): number =>
  Math.round((startOfDay(date).getTime() - startOfDay(weekStart).getTime()) / (24 * 60 * 60 * 1000));

export const getWeekNumber = (date: Date | string): number => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
};

export const isSameDay = (date1: Date | string, date2: Date | string): boolean => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return d1.getFullYear() === d2.getFullYear() &&
         d1.getMonth() === d2.getMonth() &&
         d1.getDate() === d2.getDate();
};

export const isToday = (date: Date | string): boolean => {
  return isSameDay(date, new Date());
};

export const getDateRange = (start: Date | string, end: Date | string): Date[] => {
  const dates: Date[] = [];
  const current = new Date(start);
  const endDate = new Date(end);

  while (current <= endDate) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  return dates;
};

/**
 * Parse holiday string (DD/MM/AA or DD/MM/AAAA) to Date
 */
export const parseHolidayDate = (dateStr: string | null | undefined): Date | null => {
  if (!dateStr) return null;
  const bruto = dateStr.trim();

  // ISO (YYYY-MM-DD) também vale. O campo de feriados do modal é texto livre e
  // grava o que o usuário digitar: quem colava uma lista em ISO tinha os
  // feriados aceitos sem erro e ignorados no cálculo, sem aviso nenhum. ISO não
  // tem ambiguidade de ordem, então aceitar é sempre seguro.
  const iso = bruto.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) {
    return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  }

  const parts = bruto.split('/');
  if (parts.length !== 3) return null;

  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
  let year = parseInt(parts[2], 10);

  // Handle 2-digit year
  if (year < 100) {
    year += year < 50 ? 2000 : 1900;
  }

  return new Date(year, month, day);
};

/**
 * Check if a date is in the holidays list
 */
export const isDateHoliday = (date: Date | string | null | undefined, holidays: string[] = []): boolean => {
  if (!date || !holidays || holidays.length === 0) return false;

  const d = new Date(date);
  d.setHours(0, 0, 0, 0);

  return holidays.some(holidayStr => {
    const holidayDate = parseHolidayDate(holidayStr);
    if (!holidayDate) return false;
    holidayDate.setHours(0, 0, 0, 0);
    return d.getTime() === holidayDate.getTime();
  });
};

/**
 * Parse duration string (e.g., "5d", "2w", "1m", "16h") and return number of days
 * @param {string} duration - Duration string (e.g., "5d", "16h")
 * @param {number} hoursPerDay - Hours per work day (default 8)
 */
export const parseDuration = (duration: string | null | undefined, hoursPerDay: number = 8): number => {
  if (!duration) return 1;

  const str = String(duration).toLowerCase().trim();
  const match = str.match(/^(\d+(?:\.\d+)?)\s*([dwmh]?)$/);

  if (!match) {
    // Try to parse as just a number
    const num = parseFloat(str);
    return isNaN(num) ? 1 : Math.max(1, Math.round(num));
  }

  const value = parseFloat(match[1]);
  const unit = match[2] || 'd';

  switch (unit) {
    case 'h': return Math.max(1, Math.round(value / hoursPerDay)); // hoursPerDay hours = 1 day
    case 'd': return Math.max(1, Math.round(value));
    case 'w': return Math.max(1, Math.round(value * 5)); // 5 work days per week
    case 'm': return Math.max(1, Math.round(value * 22)); // ~22 work days per month
    default: return Math.max(1, Math.round(value));
  }
};

/**
 * Calculate end date from start date and duration
 * @param {Date|string} startDate - Start date
 * @param {string} duration - Duration string (e.g., "5d", "16h")
 * @param {Array} workDays - Work days of the week (0-6)
 * @param {Array} holidays - Array of holiday date strings
 * @param {number} hoursPerDay - Hours per work day (default 8)
 */
export const calculateEndDate = (startDate: Date | string | null, duration: string | null | undefined, workDays: number[] = [1, 2, 3, 4, 5], holidays: string[] = [], hoursPerDay: number = 8): Date => {
  if (!startDate) return new Date();

  const days = parseDuration(duration, hoursPerDay);
  const start = new Date(startDate);
  let count = 0;
  const current = new Date(start);

  // Count work days until we reach the duration
  while (count < days) {
    const dayOfWeek = current.getDay();
    const isHoliday = isDateHoliday(current, holidays);

    if (workDays.includes(dayOfWeek) && !isHoliday) {
      count++;
    }

    if (count < days) {
      current.setDate(current.getDate() + 1);
    }
  }

  return current;
};

/**
 * Calculate duration in days between two dates (considering work days)
 */
export const calculateDuration = (startDate: Date | string | null, endDate: Date | string | null, workDays: number[] = [1, 2, 3, 4, 5], holidays: string[] = []): string => {
  if (!startDate || !endDate) return '1d';

  const start = new Date(startDate);
  const end = new Date(endDate);

  if (end < start) return '1d';

  let count = 0;
  const current = new Date(start);

  while (current <= end) {
    const dayOfWeek = current.getDay();
    const isHoliday = isDateHoliday(current, holidays);

    if (workDays.includes(dayOfWeek) && !isHoliday) {
      count++;
    }
    current.setDate(current.getDate() + 1);
  }

  return `${Math.max(1, count)}d`;
};
